import { logUsage } from 'owa-analytics';
import { getFileProviderAccessToken } from 'owa-fileprovider-accesstoken-service';
import { isFeatureEnabled } from 'owa-feature-flags';
import { isStringNullOrWhiteSpace } from 'owa-localize';
import type AttachmentDataProviderScope from 'owa-service/lib/contract/AttachmentDataProviderScope';
import AttachmentDataProviderType from 'owa-service/lib/contract/AttachmentDataProviderType';
import OneDriveConsumerScopeType from 'owa-service/lib/contract/OneDriveConsumerScopeType';
import OneDriveProScopeType from 'owa-service/lib/contract/OneDriveProScopeType';
import { getOneDriveConsumerScopeType } from '../utils/getOneDriveConsumerScopeType';
import { getOneDriveProScopeType } from '../utils/getOneDriveProScopeType';

async function isTokenAvailableForProvider(
    fileProviderType: AttachmentDataProviderType,
    url: string | undefined
): Promise<boolean> {
    const eventName = 'FPAccessTokenUnavailable';
    const customData = [fileProviderType];
    try {
        const token = await getFileProviderAccessToken(
            fileProviderType,
            false /* forceTokenRefresh */,
            url
        );
        if (isStringNullOrWhiteSpace(token)) {
            logUsage(eventName, customData);
            return false;
        }
        return true;
    } catch (error) {
        // If there is an error in getting the token then we consider that
        // the token is not available and hence return false
        logUsage(eventName, customData);
        return false;
    }
}

export async function canCallFileProviderAPIFromClient(
    fileProviderType: AttachmentDataProviderType,
    url: string,
    scope: AttachmentDataProviderScope
): Promise<boolean> {
    switch (fileProviderType) {
        case AttachmentDataProviderType.Box:
        case AttachmentDataProviderType.Dropbox:
        case AttachmentDataProviderType.Facebook:
            return isTokenAvailableForProvider(fileProviderType, undefined);
        case AttachmentDataProviderType.GDrive:
        // In case of GDrive we always have to make the call from client side
        // as there is no server side implementation of it
        case AttachmentDataProviderType.MailMessage:
            // No server side implementation of mail message file provider
            return true;
        case AttachmentDataProviderType.OneDrivePro:
            // only for MyDocuments there is client side call. The rest are server side implementation ( Groups, Recent, etc)
            if (
                getOneDriveProScopeType(fileProviderType, scope) ===
                    OneDriveProScopeType.MyDocuments ||
                getOneDriveProScopeType(fileProviderType, scope) === OneDriveProScopeType.Recent
            ) {
                return isTokenAvailableForProvider(fileProviderType, url);
            }
            return false;
        case AttachmentDataProviderType.OneDriveConsumer:
            if (
                getOneDriveConsumerScopeType(fileProviderType, scope) ===
                OneDriveConsumerScopeType.RecentItems
            ) {
                return true;
            }

            return isFeatureEnabled('doc-odcgetitems');
        default:
            return false;
    }
}
